import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Paper,
  Stack,
  TextField,
  Typography,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  Chip,
  Checkbox,
  ListItemText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { useParams } from "react-router-dom";
import ItemService from "../../../apis/ItemService";
import { BLOOMS_TAXONOMY_SKILLS } from "../constants/lessonMetadata";
import { normaliseTaxonomyOptions } from "../utils/taxonomy";
import { ArabicGrammarTerms } from "../utils/ArabicGrammarTerms";
import TinyEditor from "../../../custom-ckeditor/TinyEditor";

// ✅ Color map reused from QuizPage
const colorMap = [
  "#e74c3c", // deep red
  "#27ae60", // dark green
  "#3498db", // strong blue
  "#7f8c8d", // steel gray
  "#f1c40f", // golden yellow
  "#8e44ad", // royal purple
  "#2980b9", // ocean blue
  "#1abc9c", // teal green
  "#c0392b", // dark coral
  "#9b59b6", // violet purple
];

const createEmptyMarkupItem = () => ({
  version: "v1",
  dateCreated: new Date().toISOString(),
  dateUpdated: new Date().toISOString(),
  createdBy: "",
  updatedBy: "",
  Type: "Markup",
  Name: "",
  Title: "",
  Text: "",
  MarkupType: "Underline",
  Direction: "RTL",
  points: 1,
  skilltype: "",
  itemcat: "",
  Options: {}, // { word: [ { Type, Rationale: { Text } } ] }
});

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline"],
    ["link"],
    ["clean"],
  ],
};
const quillFormats = ["header", "bold", "italic", "underline", "link"];

const MarkupItemDetail = () => {
  const { itemId } = useParams();
  const isNew = !itemId || itemId === "new";

  const [item, setItem] = useState(createEmptyMarkupItem());
  const [taxonomyOptions, setTaxonomyOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false); // 🆕 Added

  const [selectedWord, setSelectedWord] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedValues, setSelectedValues] = useState([]);
  const [rationaleText, setRationaleText] = useState("");

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [taxonomyResponse, itemResponse] = await Promise.all([
          ItemService.fetchTaxonomy("arabic"),
          isNew ? Promise.resolve(null) : ItemService.fetchItemById(itemId),
        ]);

        setTaxonomyOptions(normaliseTaxonomyOptions(taxonomyResponse));

        if (itemResponse?.item) {
          const loadedItem = itemResponse.item;

          // 🟢 Convert numeric keys ("1", "2") back to actual words
          if (loadedItem?.Options && loadedItem?.Text) {
            const words = loadedItem.Text.split(" ").filter(
              (w) => w.trim() !== ""
            );
            const convertedOptions = {};

            Object.entries(loadedItem.Options).forEach(([key, value]) => {
              const index = parseInt(key, 10) - 1;
              if (index >= 0 && words[index]) {
                convertedOptions[words[index]] = value;
              }
            });

            loadedItem.Options = convertedOptions;
          }

          setItem(loadedItem);
        }
      } catch (err) {
        console.error("Failed to load item:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [itemId, isNew]);

  // Split text into words
  const wordsFromText = useMemo(
    () =>
      item.Text ? item.Text.split(" ").filter((w) => w.trim() !== "") : [],
    [item.Text]
  );

  // 🟢 Cleanup Options whenever text changes
  useEffect(() => {
    setItem((prev) => {
      const existingOptions = { ...prev.Options };
      const validWords = new Set(wordsFromText);
      const cleanedOptions = Object.fromEntries(
        Object.entries(existingOptions).filter(([word]) => validWords.has(word))
      );
      if (JSON.stringify(existingOptions) !== JSON.stringify(cleanedOptions)) {
        return { ...prev, Options: cleanedOptions };
      }
      return prev;
    });
  }, [wordsFromText]);

  // 🟢 Create color mapping dynamically
  const typeToColorMap = useMemo(() => {
    const uniqueTypes = new Set();
    Object.values(item.Options).forEach((values) =>
      values.forEach((v) => uniqueTypes.add(v.Type))
    );

    const typesArray = Array.from(uniqueTypes);
    const map = {};
    typesArray.forEach((type, i) => {
      map[type] = colorMap[i % colorMap.length];
    });
    return map;
  }, [item.Options]);

  const handleFieldChange = (field, value) => {
    setItem((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddMapping = () => {
    if (!selectedWord || !selectedCategory || selectedValues.length === 0)
      return;

    setItem((prev) => {
      const prevOptions = { ...prev.Options };
      const existing = prevOptions[selectedWord] || [];

      const newEntries = selectedValues.map((val) => ({
        Type: val,
        Rationale: { Text: rationaleText || "" },
      }));

      const updated = [
        ...existing.filter((e) => !selectedValues.includes(e.Type)),
        ...newEntries,
      ];

      return {
        ...prev,
        Options: { ...prevOptions, [selectedWord]: updated },
      };
    });

    setSelectedWord("");
    setSelectedCategory("");
    setSelectedValues([]);
    setRationaleText("");
  };

  const handleRemoveValue = (word, idx) => {
    setItem((prev) => {
      const newOptions = { ...prev.Options };
      newOptions[word].splice(idx, 1);
      if (newOptions[word].length === 0) delete newOptions[word];
      return { ...prev, Options: newOptions };
    });
  };

  const handleRationaleChange = (word, idx, text) => {
    setItem((prev) => {
      const newOptions = { ...prev.Options };
      newOptions[word][idx].Rationale.Text = text;
      return { ...prev, Options: newOptions };
    });
  };

  // Convert to numeric key structure
  const numberedOptions = useMemo(() => {
    const options = {};
    wordsFromText.forEach((word, index) => {
      if (item.Options[word]) {
        options[(index + 1).toString()] = item.Options[word];
      }
    });
    return options;
  }, [item.Options, wordsFromText]);

  const numberedItem = useMemo(
    () => ({ ...item, Options: numberedOptions }),
    [item, numberedOptions]
  );

  const jsonPreview = useMemo(
    () => JSON.stringify(numberedItem, null, 2),
    [numberedItem]
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const payload = {
        ...numberedItem,
        dateUpdated: new Date().toISOString(),
      };
      await ItemService.saveItem(payload, isNew ? undefined : itemId);
      setSaveSuccess(true);
    } catch (err) {
      console.error("Save failed:", err);
      setSaveError("Failed to save item. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [numberedItem, isNew, itemId]);

  const handleOpenJson = () => setJsonDialogOpen(true);
  const handleCloseJson = () => setJsonDialogOpen(false);

  function MyCustomUploadAdapterPlugin(editor) {
    editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
      return new MyUploadAdapter(loader);
    };
  }

  class MyUploadAdapter {
    constructor(loader) {
      this.loader = loader;
    }

    upload() {
      return this.loader.file.then(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              // Base64 string result
              resolve({ default: reader.result });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          })
      );
    }

    abort() {
      // Optional: handle aborts
    }
  }

  return (
    <Stack spacing={3}>
      {/* === BASIC INFO === */}
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label={item.Type} color="secondary" />
              <Typography variant="h5" fontWeight={600}>
                {item.Name || "Untitled Markup Item"}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={() => setPreviewDialogOpen(true)}
              >
                Preview
              </Button>
              <Button variant="outlined" onClick={handleOpenJson}>
                View JSON
              </Button>
            </Stack>
          </Stack>

          {saveSuccess && (
            <Alert severity="success" onClose={() => setSaveSuccess(false)}>
              Item saved successfully!
            </Alert>
          )}
          {saveError && <Alert severity="error">{saveError}</Alert>}
          <Divider />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Item Name"
                fullWidth
                required
                value={item.Name}
                onChange={(e) => handleFieldChange("Name", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                type="number"
                label="Points"
                fullWidth
                value={item.points}
                onChange={(e) =>
                  handleFieldChange("points", Number(e.target.value))
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Skill Type</InputLabel>
                <Select
                  value={item.skilltype}
                  onChange={(e) =>
                    handleFieldChange("skilltype", e.target.value)
                  }
                >
                  {BLOOMS_TAXONOMY_SKILLS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Item Category</InputLabel>
                <Select
                  value={item.itemcat}
                  onChange={(e) => handleFieldChange("itemcat", e.target.value)}
                >
                  {taxonomyOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Markup Type</InputLabel>
                <Select
                  value={item.MarkupType}
                  onChange={(e) =>
                    handleFieldChange("MarkupType", e.target.value)
                  }
                >
                  <MenuItem value="Underline">Underline</MenuItem>
                  <MenuItem value="Highlight">Highlight</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Direction</InputLabel>
                <Select
                  value={item.Direction}
                  onChange={(e) =>
                    handleFieldChange("Direction", e.target.value)
                  }
                >
                  <MenuItem value="RTL">Right to Left</MenuItem>
                  <MenuItem value="LTR">Left to Right</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Instructions (Title)
            </Typography>

            <TinyEditor
              value={item.Title}
              direction={item.Direction}
              onChange={(content) => handleFieldChange("Title", content)}
            />
          </Box>

          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Text (Words to Tag)
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={item.Text}
              onChange={(e) => handleFieldChange("Text", e.target.value)}
            />
          </Box>
        </Stack>
      </Paper>

      {/* === WORD → MULTIPLE VALUE MAPPINGS WITH RATIONALE === */}
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={600}>
            Word Grammar Mapping
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Select Word</InputLabel>
                <Select
                  value={selectedWord}
                  onChange={(e) => setSelectedWord(e.target.value)}
                >
                  {wordsFromText.map((word, idx) => (
                    <MenuItem key={idx} value={word}>
                      {word}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  {Object.keys(ArabicGrammarTerms).map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth disabled={!selectedCategory}>
                <InputLabel>Values</InputLabel>
                <Select
                  multiple
                  value={selectedValues}
                  onChange={(e) => setSelectedValues(e.target.value)}
                  renderValue={(selected) => selected.join(", ")}
                >
                  {selectedCategory &&
                    ArabicGrammarTerms[selectedCategory].map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Checkbox
                          checked={selectedValues.includes(opt.value)}
                        />
                        <ListItemText primary={opt.label} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={handleAddMapping}
              disabled={
                !selectedWord ||
                !selectedCategory ||
                selectedValues.length === 0
              }
            >
              Add Mapping
            </Button>
          </Box>

          {/* === Show Existing Mappings === */}
          {Object.entries(item.Options).map(([word, values]) => (
            <Paper
              key={word}
              variant="outlined"
              sx={{ p: 2, mb: 1, backgroundColor: "#fafafa" }}
            >
              <Typography fontWeight={600} sx={{ mb: 1 }}>
                {word}
              </Typography>
              {values.map((v, idx) => (
                <Box key={idx} sx={{ mb: 2 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography>
                      <b>Type:</b> {v.Type}
                    </Typography>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleRemoveValue(word, idx)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <TextField
                    fullWidth
                    label="Rationale"
                    multiline
                    minRows={2}
                    sx={{ mt: 1 }}
                    value={v.Rationale?.Text || ""}
                    onChange={(e) =>
                      handleRationaleChange(word, idx, e.target.value)
                    }
                  />
                </Box>
              ))}
            </Paper>
          ))}

          {/* === LIVE PREVIEW === */}
          <Dialog
            open={previewDialogOpen}
            onClose={() => setPreviewDialogOpen(false)}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle>Preview</DialogTitle>
            <DialogContent dividers>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "2px",
                  alignItems: "center",
                  direction: item.Direction === "RTL" ? "rtl" : "ltr",
                  fontFamily: "Traditional Arabic, serif",
                  fontSize: "1.5rem",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  padding: "10px",
                  backgroundColor: "#fff",
                }}
              >
                {wordsFromText.map((word, idx) => {
                  const entries = item.Options[word] || [];
                  const isRTL = item.Direction === "RTL";

                  // Dimensions for each box (consistent)
                  const boxWidth = 110;
                  const boxHeight = 45;

                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: "inline-flex",
                        flexDirection: "column",
                        alignItems: isRTL ? "flex-end" : "flex-start",
                        marginRight: isRTL ? 0 : "16px",
                        marginLeft: isRTL ? "16px" : 0,
                        marginBottom: "20px",
                        direction: isRTL ? "rtl" : "ltr",
                      }}
                    >
                      {/* Nested Boxes — Same Width, Same Height, Each Filled with Color */}
                      <Box
                        sx={{
                          position: "relative",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: boxWidth,
                          height: boxHeight,
                        }}
                      >
                        {[...entries].reverse().map((entry, i) => {
                          const color = typeToColorMap[entry.Type] || "#ddd";
                          const inset = i * 5; // small spacing between each color box
                          return (
                            <Box
                              key={i}
                              sx={{
                                position: "absolute",
                                top: inset,
                                left: inset,
                                right: inset,
                                bottom: inset,
                                backgroundColor: color,
                                borderRadius: "10px",
                                zIndex: i,
                              }}
                            />
                          );
                        })}

                        {/* Innermost Box with Word (first color inside) */}
                        <Box
                          sx={{
                            position: "relative",
                            zIndex: entries.length + 1,
                            width: boxWidth - entries.length * 10,
                            height: boxHeight - entries.length * 10,
                            backgroundColor:
                              entries.length > 0
                                ? typeToColorMap[entries[0].Type] || "#f0f0f0"
                                : "#f0f0f0",
                            borderRadius: "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontFamily: "Traditional Arabic, serif",
                            fontSize: "1.5rem",
                            color: "#000",
                            textAlign: isRTL ? "right" : "left",
                          }}
                        >
                          {word}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {Object.keys(typeToColorMap).length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Color Legend
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{ flexWrap: "wrap", mt: 1 }}
                  >
                    {Object.entries(typeToColorMap).map(([type, color]) => (
                      <Box
                        key={type}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <Box
                          sx={{
                            width: 18,
                            height: 18,
                            backgroundColor: color,
                            borderRadius: "3px",
                            border: "1px solid #ccc",
                          }}
                        />
                        <Typography variant="body2">{type}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>

          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* === JSON PREVIEW === */}
      <Dialog
        open={jsonDialogOpen}
        onClose={handleCloseJson}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Item JSON Preview</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}
          >
            {jsonPreview}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseJson}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default MarkupItemDetail;
